// Sub-Step 3.3 — usePublicAuth Hook für public/-Stack.
//
// Ersetzt den Mock-Hook `useMockLoggedIn` (localStorage-stub in
// ViewerChrome.tsx) durch echte OIDC-Auth-Lesung. Konsumenten sehen
// einen einheitlichen Interface, egal ob anonym, in Hydration oder
// authentifiziert.
//
// SSR-Safe: weil OidcAuthProvider via ClientOnly nur Client-Side
// mounted, gibt es während SSR + first-render keinen AuthContext.
// useAuth() würde dann werfen — wir nutzen useContext direkt mit
// dem exportierten AuthContext und fallen back auf anonym-State.
//
// API-Vertrag:
//   loggedIn       — boolean, true wenn Token aktiv
//   isLoading      — Silent-SSO oder Token-Exchange läuft noch
//   userId         — JWT `sub`-Claim (Keycloak User-ID) oder null
//   email          — JWT `email`-Claim oder null
//   accessToken    — Bearer-Token-String oder null
//   signIn(returnTo) — Login-Flow via scrolly-public-Client (PKCE)
//   signOut()       — Token-Drop + Keycloak-Logout (Server-Cookie weg)
//
// Phase-2-Strategy-Change (Sub-Auftrag 2 explizit):
//   signIn(returnTo) macht jetzt DIREKT `auth.signinRedirect` via
//   scrolly-public-Client (PKCE-S256). returnTo wird als `url_state`
//   serialisiert, OidcCallback liest es zurück und validiert gegen
//   allowed-return-domains. Vorher (Phase 1): Portal-Umweg via
//   portal.scrolly.com/signin?return_to=… — dieser Pfad ist in
//   Phase 2 eliminiert, weil scrolly-public-Client für view.scrolly
//   eigenständig authentifiziert.

import { useContext } from "react";
import { AuthContext, type AuthContextProps } from "react-oidc-context";

export interface PublicAuthState {
  /** True wenn aktive OIDC-Session via Keycloak besteht. */
  loggedIn: boolean;
  /** True während Silent-SSO oder Token-Exchange läuft. */
  isLoading: boolean;
  /** Keycloak `sub`-Claim — eindeutige User-ID, oder null. */
  userId: string | null;
  /** Email aus JWT, oder null. */
  email: string | null;
  /** Bearer-Token-String für API-Calls, oder null. */
  accessToken: string | null;
  /**
   * Triggert SignIn-Flow via scrolly-public-Client (PKCE-S256).
   *
   * Keycloak rendert echtes Login-UI wenn keine SSO-Session existiert,
   * oder übernimmt die bestehende Session (azp wird dann der initiale
   * Client, nicht scrolly-public — OAuth-Standard-Verhalten bei SSO).
   *
   * Nach erfolgreichem Token-Exchange landet der Browser auf
   * `${origin}/oidc-callback?code=...&state=...`. Die Callback-Page
   * liest `url_state.return_to` aus dem state, validiert gegen
   * allowed-return-domains, und redirected zurück.
   *
   * @param returnTo Vollständige URL zur Page, zu der nach Login
   *                 zurückgekehrt wird. Wird in url_state serialisiert
   *                 und nach Callback gegen Domain-Allowlist validiert.
   */
  signIn(returnTo: string): void;
  /** Triggert Keycloak-Session-Logout. */
  signOut(): void;
}

const ANONYMOUS_STATE: PublicAuthState = {
  loggedIn: false,
  isLoading: true, // Default: noch nicht initialisiert
  userId: null,
  email: null,
  accessToken: null,
  signIn: (_returnTo: string) => {
    void _returnTo;
    // No-op fallback wenn kein AuthContext (SSR oder pre-mount).
    // In Phase 2 wird signIn nur aus dem SignInModal aufgerufen, das
    // erst nach AuthProvider-Mount sichtbar ist — dieser Pfad erreicht
    // also keine Production-User.
  },
  signOut: () => {
    if (typeof window === "undefined") return;
    // Best-effort wenn ohne Context — pragmatic logout via Portal.
    window.location.href = `${import.meta.env.VITE_PORTAL_BASE_URL ?? "https://portal.scrolly.com"}/signin`;
  },
};

/**
 * Hook: Auth-State für public/-Stack.
 *
 * Sicher zu rufen aus jeder Komponente — gibt ANONYMOUS_STATE wenn
 * AuthContext fehlt (SSR oder pre-mount).
 */
export function usePublicAuth(): PublicAuthState {
  // useContext returnt undefined wenn kein Provider — react-oidc-context
  // setzt einen default-export-Context, dessen Default-Value `undefined`
  // ist. So crasht es nicht während SSR-Render.
  const ctx = useContext(AuthContext) as AuthContextProps | undefined;

  if (!ctx) {
    // SSR oder before AuthProvider mounted — anonymer State.
    return ANONYMOUS_STATE;
  }

  const user = ctx.user;
  return {
    loggedIn: ctx.isAuthenticated,
    isLoading: ctx.isLoading,
    userId: (user?.profile?.sub as string | undefined) ?? null,
    email: (user?.profile?.email as string | undefined) ?? null,
    accessToken: user?.access_token ?? null,
    signIn: (returnTo: string) => {
      // Phase-2-Strategy: direkter scrolly-public-Login (kein Portal-Umweg).
      // url_state ist Keycloak-OIDC-Standard für state-payload — wird im
      // Authorize-Request mitgeschickt und nach erfolgreichem Login zurück
      // an die Callback-Page übergeben.
      // OidcCallback liest `auth.user.url_state` aus und validiert gegen
      // allowed-return-domains-Allowlist (Open-Redirect-Schutz).
      void ctx.signinRedirect({
        url_state: JSON.stringify({ return_to: returnTo }),
      });
    },
    signOut: () => {
      // Triggert Keycloak-Session-Logout via end_session_endpoint.
      void ctx.signoutRedirect();
    },
  };
}
